import { CommonModule } from '@angular/common';
import {
	Component,
	DestroyRef,
	EventEmitter,
	Input,
	Output,
	Signal,
	computed,
	effect,
	inject,
	signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LucideAngularModule } from 'lucide-angular';
import { EMPTY, catchError, debounceTime, finalize, of, switchMap, tap } from 'rxjs';

import {
	CallejeroResult,
	CallejeroService
} from '../../../../core/services/callejero.service';

import { PARTIDOS } from '../../../../shared/statics/partidos';
import { FormErrorsPipe } from '../../../../shared/utils/pipes/form-errors.pipe';

type Partido = {
	id: number;
	description: string;
};

@Component({
	selector: 'app-register-address',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatCardModule,
		MatFormFieldModule,
		MatInputModule,
		MatAutocompleteModule,
		MatProgressSpinnerModule,
		LucideAngularModule,
		FormErrorsPipe
	],
	templateUrl: './register-address.html',
	styleUrl: './register-address.scss'
})
export class RegisterAddressComponent {
	@Input({ required: true }) requestForm!: Signal<boolean>;
	@Output() addressChange = new EventEmitter<any>();

	private readonly fb = inject(FormBuilder);
	private readonly destroyRef = inject(DestroyRef);
	private readonly callejeroService = inject(CallejeroService);

	readonly partidos: Partido[] = PARTIDOS;

	readonly callejeroSugerencias = signal<CallejeroResult[]>([]);
	readonly isLoadingCallejero = signal(false);
	readonly partidoBusqueda = signal('');
	readonly isVicenteLopez = signal(false);

	private readonly selectedCalle = signal('');
	private readonly selectedNumero = signal('');

	private lastSelectedDireccion = '';

	readonly addressForm: FormGroup = this.fb.group({
		partido: [null, Validators.required],
		partidoBusqueda: [null, Validators.required],

		direccion: [{ value: '', disabled: true }, [Validators.required, Validators.minLength(5)]],
		localidad: ['', [Validators.required, Validators.minLength(2)]],
		codPostal: ['', [Validators.required, Validators.pattern(/^\d{4}$/)]],

		torre: [''],
		piso: [''],
		depto: ['']
	});

	readonly partidosFiltrados = computed(() => {
		const value = this.partidoBusqueda().toLowerCase();

		return value
			? this.partidos.filter((p) => p.description.toLowerCase().includes(value))
			: this.partidos.slice(0, 20);
	});

	constructor() {
		this.handleRequestForm();
		this.listenDireccionChanges();
		this.listenPartidoBusquedaChanges();
	}

	displayDireccion = (value: CallejeroResult | string | null): string => {
		if (!value) {
			return '';
		}

		if (typeof value === 'string') {
			return value;
		}

		const road = value.address?.road ?? value.display_name?.split(', ')[0]?.trim() ?? '';
		const number = value.address?.house_number ?? '';

		return number ? `${road} ${number}` : road;
	};

	displayPartido = (partido: Partido | null): string => partido?.description ?? '';

	onSeleccionarPartido(partido: Partido): void {
		const isVL = partido.description === 'VICENTE LÓPEZ';

		this.isVicenteLopez.set(isVL);
		this.addressForm.get('direccion')!.enable();

		if (!isVL) {
			this.callejeroSugerencias.set([]);
		}

		this.addressForm.patchValue({
			partido: partido.description,
			partidoBusqueda: partido
		});
	}

	onSeleccionarSugerencia(result: CallejeroResult): void {
		const { direccion, calle, numero, localidad } = this.parseDireccionResult(result);

		this.selectedCalle.set(calle);
		this.selectedNumero.set(numero);
		this.lastSelectedDireccion = direccion;

		this.addressForm.patchValue({ localidad });
		this.callejeroSugerencias.set([]);
	}

	private handleRequestForm(): void {
		effect(() => {
			if (this.requestForm()) {
				this.trySubmit();
			}
		});
	}

	private listenDireccionChanges(): void {
		this.addressForm
			.get('direccion')!
			.valueChanges.pipe(
				takeUntilDestroyed(this.destroyRef),
				tap((value) => {
				if (value !== null && typeof value !== 'string') return;
				this.resetDireccionSelection();
			}),
				debounceTime(500),
				switchMap((value) => this.searchDirecciones(value))
			)
			.subscribe((results) => {
				this.callejeroSugerencias.set(results);
			});
	}

	private listenPartidoBusquedaChanges(): void {
		this.addressForm
			.get('partidoBusqueda')!
			.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((value) => {
				this.partidoBusqueda.set(
					typeof value === 'string' ? value : (value?.description ?? '')
				);
			});
	}

	private resetDireccionSelection(): void {
		this.lastSelectedDireccion = '';
		this.selectedCalle.set('');
		this.selectedNumero.set('');
	}

	private searchDirecciones(value: unknown) {
		if (!this.isVicenteLopez()) {
			return EMPTY;
		}

		const query = this.normalizeDireccionQuery(value);

		if (query.length < 5 || query === this.lastSelectedDireccion) {
			this.callejeroSugerencias.set([]);
			return EMPTY;
		}

		this.isLoadingCallejero.set(true);

		return this.callejeroService.search(query).pipe(
			finalize(() => this.isLoadingCallejero.set(false)),
			catchError(() => of([]))
		);
	}

	private normalizeDireccionQuery(value: unknown): string {
		if (typeof value === 'string') {
			return value.trim();
		}

		return this.displayDireccion(value as CallejeroResult).trim();
	}

	private parseDireccionResult(result: CallejeroResult) {
		const parts = result.display_name.split(', ');

		const numero = result.address?.house_number ?? '';

		const calle = result.address?.road ?? parts[0]?.trim() ?? '';

		const localidad = result.address?.city_district ?? parts[1]?.trim() ?? '';

		return {
			numero,
			calle,
			localidad,
			direccion: numero ? `${calle} ${numero}` : calle
		};
	}

	private trySubmit(): void {
		if (this.addressForm.invalid) {
			this.addressForm.markAllAsTouched();
			return;
		}

		const value = this.addressForm.getRawValue();

		const direccion = this.selectedCalle()
			? {
					calle: this.selectedCalle(),
					numero: this.selectedNumero()
				}
			: this.parseManualDireccion(value.direccion);

		this.addressChange.emit({
			tipoDomicilio: '2',
			calle: direccion.calle,
			numero: direccion.numero,
			localidad: value.localidad,
			partido: value.partido,
			cp: String(value.codPostal),
			torre: value.torre || '',
			piso: value.piso || '',
			depto: value.depto || ''
		});
	}

	private parseManualDireccion(direccion: string) {
		const partes = direccion.trim().split(/\s+/);

		const lastPart = partes[partes.length - 1];

		const numero = /^\d+$/.test(lastPart) ? partes.pop()! : '';

		return {
			numero,
			calle: partes.join(' ')
		};
	}
}
